import { Injectable, signal, effect, computed } from '@angular/core';

/**
 * Notification type for toast messages.
 */
export type NotificationType = 'success' | 'danger' | 'warning' | 'info';

/**
 * Notification interface for toast messages.
 */
export interface Notification {
  id: number;
  message: string;
  type: NotificationType;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  // Internal signal to manage the notification list
  private _notifications = signal<Notification[]>([]);
  // Private counter for unique notification IDs
  private _counter = 0;

  /**
   * Exposes a readonly signal for UI components to subscribe reactively.
   */
  readonly notifications = this._notifications.asReadonly();

  /**
   * Show a new notification toast.
   * @param message The message to display
   * @param type The notification type (default: 'info')
   */
  show(message: string, type: NotificationType = 'info'): void {
    const id = ++this._counter;
    const notification: Notification = { id, message, type };
    this._notifications.set([...this._notifications(), notification]);
    setTimeout(() => this.close(id), 3000);
  }

  /**
   * Close (remove) a notification by its ID.
   * @param id The notification ID
   */
  close(id: number): void {
    this._notifications.set(this._notifications().filter(n => n.id !== id));
  }

  /**
   * Get the current list of notifications (readonly).
   */
  getAll(): readonly Notification[] {
    return this._notifications();
  }
}
